import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, AlertCircle, FileSignature, Download, Printer } from 'lucide-react';
import { publicContractApi } from '../services/vendorContractService';
import ContractBodyHtml from '../components/vendor/contract/ContractBodyHtml';
import SignaturePad from '../components/vendor/contract/SignaturePad';
import Button from '../components/shared/Button';
import { downloadContractPdf } from '../utils/vendorContractPdf';

const ContractSign = () => {
  const { token } = useParams();
  const printRef = useRef(null);
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [agree, setAgree] = useState(false);
  const [formError, setFormError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['public-contract', token],
    queryFn: () => publicContractApi.getByToken(token),
    enabled: !!token,
    retry: false,
  });

  const contract = data?.data;
  const loadError = error?.response?.data?.message || error?.message;

  const signMutation = useMutation({
    mutationFn: (payload) => publicContractApi.sign(token, payload),
    onSuccess: () => {
      refetch();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onError: (err) => {
      setFormError(err?.response?.data?.message || err.message || 'Could not sign.');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!signerName.trim() || signerName.trim().length < 2) {
      setFormError('Enter your full legal name.');
      return;
    }
    if (!signatureData) {
      setFormError('Please draw your signature.');
      return;
    }
    if (!agree) {
      setFormError('Confirm that you agree to this contract.');
      return;
    }
    signMutation.mutate({
      signer_name: signerName.trim(),
      signature_data: signatureData,
      agree: true,
    });
  };

  const handleDownloadPdf = async () => {
    if (!contract) return;
    setFormError('');
    setPdfLoading(true);
    try {
      await downloadContractPdf(contract);
    } catch (e) {
      console.error(e);
      setFormError('Could not create PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!contract && loadError) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 max-w-md w-full p-8 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Unable to open contract</h1>
          <p className="text-sm text-slate-600">{loadError}</p>
        </div>
      </div>
    );
  }

  const isSigned = contract?.status === 'signed';
  const canSign = contract?.can_be_signed && !isSigned;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 overflow-x-hidden">
      <div className="max-w-3xl mx-auto space-y-4 min-w-0 w-full">
        <div className="text-center mb-2">
          {(contract?.logo_url || contract?.vendor?.logo_url) && (
            <img
              src={contract.logo_url || contract.vendor.logo_url}
              alt=""
              className="h-12 w-12 rounded-lg object-cover border border-slate-200 mx-auto mb-3"
            />
          )}
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {contract?.vendor?.business_name || 'Contract'}
          </p>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">{contract?.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{contract?.contract_number}</p>
        </div>

        {isSigned && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 contract-no-print">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">Contract signed</p>
                <p className="text-sm text-emerald-800 mt-0.5">
                  Signed by {contract.signer_name}
                  {contract.signed_at
                    ? ` on ${new Date(contract.signed_at).toLocaleString()}`
                    : ''}
                  . Download a PDF or print a copy for your records.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                type="button"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                loading={pdfLoading}
                icon={Download}
              >
                {pdfLoading ? 'Preparing PDF…' : 'Download PDF'}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={handlePrint} icon={Printer}>
                Print
              </Button>
            </div>
          </div>
        )}

        <div
          ref={printRef}
          id="contract-print-area"
          className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 overflow-hidden min-w-0 w-full"
        >
          <div className="hidden print:block text-center mb-6">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {contract?.vendor?.business_name || 'Contract'}
            </p>
            <h1 className="text-xl font-bold text-slate-900">{contract?.title}</h1>
            <p className="text-sm text-slate-500">{contract?.contract_number}</p>
          </div>
          <ContractBodyHtml html={contract?.body_html} />
          {contract?.vendor_signature_data && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Vendor signature</p>
              <p className="text-sm text-slate-700 mb-2">
                Signed by{' '}
                <strong>
                  {contract.vendor_signer_name || contract?.vendor?.business_name || 'Vendor'}
                </strong>
                {contract.vendor_signed_at
                  ? ` on ${new Date(contract.vendor_signed_at).toLocaleString()}`
                  : ''}
              </p>
              <img
                src={contract.vendor_signature_data}
                alt="Vendor signature"
                className="max-w-xs border border-slate-200 rounded-lg"
              />
            </div>
          )}
          {isSigned && contract.signature_data && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Client signature</p>
              <p className="text-sm text-slate-700 mb-2">
                Signed by <strong>{contract.signer_name}</strong>
                {contract.signed_at
                  ? ` on ${new Date(contract.signed_at).toLocaleString()}`
                  : ''}
              </p>
              <img
                src={contract.signature_data}
                alt="Client signature"
                className="max-w-xs border border-slate-200 rounded-lg"
              />
            </div>
          )}
        </div>

        {canSign && (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4 contract-no-print">
            <div className="flex items-center gap-2 mb-1">
              <FileSignature className="w-5 h-5 text-slate-700" />
              <h2 className="text-lg font-semibold text-slate-900">Sign online</h2>
            </div>
            <p className="text-sm text-slate-600">
              Draw your signature below. This has the same legal effect as a handwritten signature for this agreement.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full legal name</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Type your name as it should appear"
                autoComplete="name"
              />
            </div>
            <SignaturePad onChange={setSignatureData} />
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-1"
              />
              <span>
                I have read this contract and agree to its terms. I am authorized to sign on behalf of{' '}
                {contract.client_name || 'the client'}.
              </span>
            </label>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <Button type="submit" loading={signMutation.isPending} disabled={signMutation.isPending}>
              Sign contract
            </Button>
          </form>
        )}

        {!canSign && !isSigned && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 contract-no-print">
            This contract cannot be signed right now (it may be cancelled or expired). Contact the vendor for a new link.
          </div>
        )}
      </div>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #contract-print-area, #contract-print-area * { visibility: visible !important; }
          #contract-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .contract-no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default ContractSign;
